import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Grid, Input, InputNumber, Select, Switch } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import { PATTERN_NAME } from '@/utils/regexp';

import { PluginModal } from '../../components/PluginModal';
import { getModalPopupContainer } from '../../utils/modalPopupContainer';
import { APP_INSTANCE_USAGE_SCENARIO_OPTIONS, resolveUsageScenarioPortPathOnChange } from './constants';
import styles from './index.module.less';
import type { ServicePortModalProps } from './types';

const FormItem = Form.Item;
const Row = Grid.Row;
const Col = Grid.Col;
const { TextArea } = Input;

interface ServicePortModalFormProps extends ServicePortModalProps {
  form: FormInstance;
}

/** 服务端口添加/编辑弹窗 */
export function ServicePortModal({
  visible,
  form,
  isDefault,
  usageScenarioOptions = APP_INSTANCE_USAGE_SCENARIO_OPTIONS,
  onOk,
  onCancel,
}: ServicePortModalFormProps) {
  const { t } = useTranslation();
  const prevUsageScenarioRef = useRef<string | undefined>();

  useEffect(() => {
    if (visible) {
      prevUsageScenarioRef.current = form.getFieldValue('usageScenario') as string | undefined;
    }
  }, [visible, form]);

  /** 服务名称：必填（内置服务除外），且须符合 DNS 子域命名规范 */
  const nameRules = useMemo(
    () =>
      isDefault
        ? []
        : [
            { required: true, message: t('common:required.field') },
            { match: PATTERN_NAME, message: t('cluster:input-name-tips') },
          ],
    [isDefault, t],
  );

  const usageScenarioRules = useMemo(() => (isDefault ? [] : [{ required: true, message: t('common:required.field') }]), [isDefault, t]);

  const internalPortRules = useMemo(() => [{ required: true, message: t('common:required.field') }], [t]);

  return (
    <PluginModal
      title={t('data-center:maas.inference.service.title')}
      visible={visible}
      onOk={() => onOk()}
      onCancel={onCancel}
      unmountOnExit
      style={{ width: 640 }}
    >
      <Form form={form} layout="vertical" className={styles.modalBody}>
        <Row gutter={16}>
          <Col span={12}>
            <FormItem label={t('data-center:maas.service.name')} field="name" rules={nameRules} disabled={isDefault}>
              <Input placeholder={t('data-center:maas.service.name')} />
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem label={t('data-center:usage.scenario')} field="usageScenario" rules={usageScenarioRules}>
              <Select
                options={usageScenarioOptions}
                placeholder={t('data-center:please.select.usage.scenario')}
                getPopupContainer={getModalPopupContainer}
                onChange={(value) => {
                  const previousScenario = prevUsageScenarioRef.current;
                  prevUsageScenarioRef.current = value as string;
                  const portPathChange = resolveUsageScenarioPortPathOnChange(previousScenario, value as string);
                  if (portPathChange) {
                    form.setFieldValue('routerPath', portPathChange.path);
                    if (portPathChange.port != null) {
                      form.setFieldValue('internalPort', portPathChange.port);
                    }
                  }
                }}
              />
            </FormItem>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <FormItem label={t('common:internal.port')} field="internalPort" rules={internalPortRules}>
              <InputNumber min={1} max={65535} style={{ width: '100%' }} placeholder={t('common:internal.port')} />
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem label={t('common:path')} field="routerPath" disabled={isDefault}>
              <Input placeholder={t('common:input.placeholder.name', { name: t('common:path') })} />
            </FormItem>
          </Col>
        </Row>
        <FormItem label={t('common:description')} field="description" disabled={isDefault}>
          <TextArea placeholder={t('common:description.placeholder')} autoSize={{ minRows: 2, maxRows: 4 }} />
        </FormItem>
        <FormItem field="_isDefault" hidden triggerPropName="checked">
          <Switch />
        </FormItem>
      </Form>
    </PluginModal>
  );
}
